/*
  Shared prompt builder for agents.
  - Dynamically imports agent assets from `./<agentName>/index`.
  - Builds a prompt from: input data + instructions + available recommendations.
  - Calls OpenAI Chat Completions (simple wrapper).

  NOTE: For production, run OpenAI calls server-side. This file is a convenience scaffold.
*/

type AgentAssets = {
  instruction: any;
  input_apis?: any;
  reporting_recommendations?: any[];
  perform_recommendations?: any[];
};

export async function loadAgentAssets(agentName: string): Promise<AgentAssets> {
  try {
    // dynamic import from agents/<agentName>/index
    const mod = await import(`./${agentName}/index`);
    return {
      instruction: mod.instruction ?? null,
      input_apis: mod.input_apis ?? null,
      reporting_recommendations: mod.reporting_recommendations ?? [],
      perform_recommendations: mod.perform_recommendations ?? [],
    };
  } catch (err) {
    console.warn('Failed to load local agent assets', agentName, err);
    // Fallback: try to fetch assets from backend API so assets can be served server-side
    try {
      const baseApi = (process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001').replace(/\/+$/,'');
      const url = baseApi.endsWith('/api') ? `${baseApi}/agents/assets/${agentName}` : `${baseApi}/api/agents/assets/${agentName}`;
      return fetch(url)
        .then((r) => r.json())
        .then((j) => {
          if (!j || !j.data) throw new Error('No asset data returned from server');
          const instr = j.data.instructions ?? null;
          const perf = j.data.perform_recommendations ?? j.data.perform ?? null;
          return {
            instruction: instr,
            input_apis: null,
            reporting_recommendations: [],
            perform_recommendations: perf,
          } as AgentAssets;
        });
    } catch (fetchErr) {
      console.error('Failed to fetch agent assets from backend', fetchErr);
      throw err;
    }
  }
}

export function buildPromptFromAssets(agentName: string, assets: AgentAssets, inputContext: any) {
  const lines: string[] = [];
  lines.push(`Agent: ${agentName}`);
  if (assets.instruction) {
    lines.push('\n== Instructions ==');
    if (typeof assets.instruction === 'string') lines.push(assets.instruction);
    else lines.push(JSON.stringify(assets.instruction)); // compact to save tokens
  }
  lines.push('\n== Input context ==');
  if (inputContext) lines.push(JSON.stringify(inputContext)); // compact to save tokens

  if (assets.reporting_recommendations && assets.reporting_recommendations.length) {
    lines.push('\n== Reporting recommendations (choose a few) ==');
    assets.reporting_recommendations.forEach((r: any) => lines.push(`- (${r.id}) ${r.name}`));
  }
  // Normalize perform_recommendations: agent assets may export either an
  // array (directly) or an object with an `actions` array (common pattern).
  const performList: any[] = Array.isArray(assets.perform_recommendations)
    ? assets.perform_recommendations
    : (assets.perform_recommendations && (assets.perform_recommendations as any).actions)
      ? (assets.perform_recommendations as any).actions
      : [];

  if (performList && performList.length) {
    // Include a compact canonical JSON array as the single source-of-truth.
    // Do NOT emit a separate human-readable list (avoids duplication).
    lines.push('\n== Performable actions JSON (SOURCE OF TRUTH) ==');
    try {
      // compact JSON to reduce token usage
      lines.push(JSON.stringify(performList));
    } catch (e) {
      // fallback to simple listing if JSON stringify fails for any reason
      performList.forEach((r: any) => lines.push(JSON.stringify(r)));
    }

    lines.push('\nIMPORTANT: The JSON above is the canonical list of available actions. When recommending performable actions, only use ids that exist in that JSON. Do NOT invent ids or reference items not present in the JSON.');
  }

  lines.push('\nPlease provide structured recommendations with reason and reference to the action id(s).');
  lines.push('\nTo minimize tokens, PREFER the compact TOON format instead of full JSON. TOON format (preferred):');
  lines.push('  - First line: `PLAN: <short plan>`');
  lines.push('  - Following lines: one recommended action per line using a pipe-separated compact record: `id|code|text|reason`');
  lines.push('Example:');
  lines.push('PLAN: Focus on revenue cutoff, AR aging and inventory valuation given the stock clearance and new sales channel.');
  lines.push('204|PERF-PLANNER-004|Extract accounts receivable listing|AR balance and aging require confirmation due to new channel');
  lines.push('\nOnly use ids from the Performable actions JSON above. If you cannot use TOON for any reason, you MAY fallback to the compact JSON form: {"plan":"...","recommended_actions":[{"id":201,"code":"PERF-PLANNER-001","text":"...","reason":"..."}]}');
  lines.push('Do NOT include any surrounding markdown fences or extra commentary.');
  return lines.join('\n');
}

// Improved prompt that asks the assistant to return a strict JSON following this schema:
// {
//   "plan": "<text explaining how to plan the audit based on the input_context>",
//   "recommended_actions": [
//     { "id": <integer>, "text": "<short action text>", "reason": "<why recommended>" }
//   ]
// }
// The assistant MUST return only valid JSON (no surrounding commentary). Use the IDs shown above when referring to actions.

export async function callOpenAI(prompt: string, model = 'gpt-4o') {
  // Use backend proxy to avoid exposing API keys in the browser.
  // Allow disabling OpenAI calls during prompt development/testing.
  // Set REACT_APP_DISABLE_OPENAI=1 in the frontend environment to skip backend calls.
  if (process.env.REACT_APP_DISABLE_OPENAI === '1') {
    // Return a lightweight sentinel object so callers can detect the disabled mode.
    return { mock: true, message: 'OpenAI calls disabled by REACT_APP_DISABLE_OPENAI' } as any;
  }

  const baseApi = (process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001').replace(/\/+$/,'');
  const url = `${baseApi}/agents/openai`;

  const payload = { prompt, model, max_tokens: 800, temperature: 0.2 };

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`OpenAI proxy request failed: ${res.status} ${txt}`);
  }

  const data = await res.json();
  return data;
}

export async function buildAndQuery(agentName: string, inputContext: any, model?: string) {
  const assets = await loadAgentAssets(agentName);
  const prompt = buildPromptFromAssets(agentName, assets, inputContext);
  const resp = await callOpenAI(prompt, model);
  return { prompt, resp };
}
